import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { X, Send, User, FileText, CheckCircle, Loader2, Users, Copy } from 'lucide-react';
import { useMail } from '../hooks/useMail';
import { accountApi, UserSearchResult } from '../services/api';
import { ShareType } from '../types/documentSharing';

interface SendMailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  knowledgeItemId: string;
  documentTitle: string;
  documentType?: string; // Тип документа для определения доступных опций
  onDocumentShared?: (knowledgeItemId: string) => void; // Callback для уведомления об успешном шаринге
}

export const SendMailDialog: React.FC<SendMailDialogProps> = ({
  isOpen,
  onClose,
  knowledgeItemId,
  documentTitle,
  documentType,
  onDocumentShared
}) => {
  const [recipientUsername, setRecipientUsername] = useState('');
  const [text, setText] = useState('');
  const [shareType, setShareType] = useState<ShareType>(ShareType.Send);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [userFound, setUserFound] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Определяем, поддерживает ли документ совместный доступ
  const supportsSharedAccess = documentType && 
    ['content-plan', 'roadmap', 'reels', 'summary', 'audience', 'kanban'].includes(documentType.toLowerCase());

  // Логирование изменений userFound
  useEffect(() => {
    console.log('userFound changed to:', userFound);
  }, [userFound]);
  const { sendMail } = useMail();

  // Дебаунс для поиска пользователей
  const debounceSearch = useCallback(
    (query: string) => {
      const timeoutId = setTimeout(async () => {
        if (query.length >= 2) {
          setIsSearching(true);
          try {
            const response = await accountApi.searchUsers(query);
            setUserFound(response.success);
          } catch (error) {
            console.error('Ошибка поиска пользователей:', error);
            setUserFound(false);
          } finally {
            setIsSearching(false);
          }
        } else {
          setUserFound(null);
          setIsSearching(false);
        }
      }, 300);

      return timeoutId;
    },
    []
  );

  // Обработчик изменения поля ввода
  useEffect(() => {
    const timeoutId = debounceSearch(recipientUsername);
    return () => clearTimeout(timeoutId);
  }, [recipientUsername, debounceSearch]);

  const handleInputChange = (value: string) => {
    setRecipientUsername(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userFound || !recipientUsername.trim()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage(null);
      await sendMail(recipientUsername, knowledgeItemId, text, shareType);
      setRecipientUsername('');
      setText('');
      setUserFound(null);
      onClose();
      
      // Вызываем колбэк только при успешном шаринге с совместным доступом
      if (shareType === ShareType.ShareAccess) {
        onDocumentShared?.(knowledgeItemId);
      }
    } catch (error: any) {
      const message = (error?.message as string) || 'Произошла ошибка. Попробуйте позже.';
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setRecipientUsername('');
      setText('');
      setShareType(ShareType.Send);
      setUserFound(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Затемненный фон */}
      <motion.div
        className="absolute inset-0 bg-black/50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      />
      
      {/* Модальное окно */}
      <motion.div
        className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.2 }}
      >
        {/* Заголовок */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            Поделиться документом
          </h3>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {errorMessage && (
          <div className="mx-6 mt-4 mb-0 p-3 rounded-md bg-red-50 text-red-700 text-sm dark:bg-red-900/20 dark:text-red-300">
            {errorMessage}
          </div>
        )}

        {/* Информация о документе */}
        <div className="p-2 pl-6">
            <span className="text-sm text-gray-500 dark:text-gray-400">Документ:</span>
          <h4 className="text-lg font-medium">
            {documentTitle}
          </h4>
        </div>

        {/* Форма */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Выбор типа шаринга */}
          {supportsSharedAccess && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Тип доступа
              </label>
              <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => setShareType(ShareType.Send)}
                  disabled={isSubmitting}
                  className={`flex-1 flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-all
                    ${shareType === ShareType.Send 
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow' 
                      : 'bg-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Отправить копию
                </button>
                <button
                  type="button"
                  onClick={() => setShareType(ShareType.ShareAccess)}
                  disabled={isSubmitting}
                  className={`flex-1 flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-all
                    ${shareType === ShareType.ShareAccess 
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow' 
                      : 'bg-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Совместный доступ
                </button>
              </div>
            </div>
          )}

          {/* Поле получателя с поиском */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Получатель
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={recipientUsername}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder="Введите ник пользователя"
                className={`w-full pl-10 pr-10 py-3 border rounded-lg 
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         disabled:opacity-50 disabled:cursor-not-allowed transition-all
                         ${userFound 
                           ? 'border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-900/20' 
                           : 'border-gray-300 dark:border-gray-600'
                         }`}
                disabled={isSubmitting}
                required
              />
              
              {/* Индикатор загрузки или статуса */}
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                {isSearching ? (
                  <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                ) : userFound ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : null}
              </div>
            </div>

            
            {userFound === false && (
              <div className="mt-2 text-left text-red-500 text-xs">
                Пользователь не найден
              </div>
            )}
          </div>

          {/* Поле сопроводительного текста */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Сообщение
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Добавьте комментарий к документу..."
              rows={3}
              className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg 
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent
                       disabled:opacity-50 disabled:cursor-not-allowed resize-none transition-all"
              disabled={isSubmitting}
            />
          </div>

          {/* Кнопки */}
          <div className="flex space-x-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 
                       hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors font-medium
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={!userFound || isSubmitting}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 
                       bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Отправка...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>
                    Отправить
                  </span>
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}; 